import { useState, useCallback } from 'react';

interface TrimResult {
  blob: Blob;
  url: string;
}

export const useAudioTrimmer = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimAudio = useCallback(
    async (
      file: File,
      startTime: number,
      endTime: number
    ): Promise<TrimResult | null> => {
      setIsProcessing(true);
      setError(null);

      try {
        const audioContext = new AudioContext();
        const arrayBuffer = await file.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        const sampleRate = audioBuffer.sampleRate;
        const numberOfChannels = audioBuffer.numberOfChannels;

        const startSample = Math.floor(startTime * sampleRate);
        const endSample = Math.floor(endTime * sampleRate);
        const trimmedLength = endSample - startSample;

        // Create a new buffer for the trimmed audio
        const trimmedBuffer = audioContext.createBuffer(
          numberOfChannels,
          trimmedLength,
          sampleRate
        );

        // Copy the audio data
        for (let channel = 0; channel < numberOfChannels; channel++) {
          const sourceData = audioBuffer.getChannelData(channel);
          const trimmedData = trimmedBuffer.getChannelData(channel);

          for (let i = 0; i < trimmedLength; i++) {
            trimmedData[i] = sourceData[startSample + i];
          }
        }

        // Convert to WAV
        const wavBlob = audioBufferToWav(trimmedBuffer);
        const url = URL.createObjectURL(wavBlob);

        await audioContext.close();

        return { blob: wavBlob, url };
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to trim audio';
        setError(message);
        return null;
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  return { trimAudio, isProcessing, error, setError };
};

// Convert AudioBuffer to WAV Blob
function audioBufferToWav(buffer: AudioBuffer): Blob {
  const numberOfChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length * numberOfChannels * 2;
  const arrayBuffer = new ArrayBuffer(44 + length);
  const view = new DataView(arrayBuffer);

  // WAV header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + length, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numberOfChannels * 2, true);
  view.setUint16(32, numberOfChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, length, true);

  // Write audio data
  const offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const sample = buffer.getChannelData(channel)[i];
      const clipped = Math.max(-1, Math.min(1, sample));
      const int16 = clipped < 0 ? clipped * 0x8000 : clipped * 0x7fff;
      view.setInt16(offset + (i * numberOfChannels + channel) * 2, int16, true);
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

function writeString(view: DataView, offset: number, string: string): void {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}
